import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import { supabase, getProfile, updateProfile } from '../lib/supabase'
import { getMyRole, hasScope as scopeIncludes } from '../lib/roles'
import { isHardcodedAdminEmail } from '../lib/memory'
import { setCurrentUserId } from '../lib/claude'

const Ctx = createContext({})

export function AuthProvider({ children }) {
  const [user, setUser]             = useState(null)
  const [profile, setProfile]       = useState(null)
  // Role row from user_roles table — { role, scopes } or null.
  // Loaded alongside profile on auth state change.
  const [roleRow, setRoleRow]       = useState(null)
  const [loading, setLoading]       = useState(true)
  // True once we know the initial auth state (session restored, or
  // confirmed none). Distinct from `loading`, which also covers the
  // profile/role fetch. The host redirect gates on this so a signed-in
  // visitor on the apex bounces to the app subdomain the instant the
  // session is known, without waiting for the (soon-to-be-discarded)
  // profile load to finish.
  const [sessionReady, setSessionReady] = useState(false)
  const [sessionExpired, setSessionExpired] = useState(false)

  // Keep a ref to the last known user so we can check if a sign-out was forced
  const prevUserRef = useRef(null)

  const loadProfile = useCallback(async (uid) => {
    if (!uid) { setProfile(null); setRoleRow(null); setLoading(false); return }
    try {
      // Fetch role + profile in parallel — independent reads.
      getMyRole().then(setRoleRow).catch(() => setRoleRow(null));
      const p = await getProfile(uid)
      // Only blank the profile when we know the row is missing (p === null
      // from a successful query). If the fetch errors transiently, hold
      // onto the previous profile so the UI doesn't flash empty fields
      // (e.g. after the user saves and the editor is about to reopen).
      if (p) setProfile(p)

      // Default-public nudge: only fire when the column is unset (null /
      // undefined). Explicit `false` means the user opted into a private
      // profile via the editor — respect that and don't flip it back on.
      if (p && (p.marketplace_visible === null || p.marketplace_visible === undefined)) {
        // Fire-and-forget. If the nudge fails (race with concurrent
        // edit, transient RLS hiccup) we'll just try again next load.
        updateProfile(uid, { marketplace_visible: true }).catch(() => {})
      }

      // Mini-spec #7: detect the user's timezone on first profile load
      // and persist it so the daily-briefing cron can deliver at the
      // right local hour. Only writes when the column is unset or stuck
      // at the 'UTC' default — never overrides a value the user has
      // already set themselves via the Profile Editor.
      if (p && (!p.timezone || p.timezone === 'UTC')) {
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
          if (tz && tz !== 'UTC') {
            // Fire-and-forget — same pattern as the marketplace nudge.
            updateProfile(uid, { timezone: tz }).catch(() => {})
          }
        } catch { /* old browsers — leave as UTC */ }
      }
    } catch (e) {
      // Don't wipe the existing profile on a transient error — that causes
      // the Profile Editor to reopen with blank fields after a save if
      // the post-save reload hits a hiccup. Log loud and move on.
      console.error('Profile load error (keeping previous profile):', e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!supabase) { setLoading(false); setSessionReady(true); return }

    // Single source of truth for auth state. onAuthStateChange fires an
    // `INITIAL_SESSION` event immediately on subscribe (supabase-js v2)
    // carrying the restored session, so we don't call getSession()
    // separately — doing both ran the profile + role fetch twice on
    // every cold load, which was a big chunk of the "slow to log in"
    // latency.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const u = session?.user ?? null

      // We now know the initial auth state — let the host redirect act.
      setSessionReady(true)

      if (event === 'TOKEN_REFRESHED' && !session) {
        // Token refresh failed — treat as expired
        setSessionExpired(true)
      } else if (event === 'SIGNED_OUT') {
        if (prevUserRef.current !== null) {
          // They were logged in before; if it wasn't a manual sign-out
          // we detect force-logout by checking if sessionExpired was already set
          // We leave sessionExpired management to the explicit signOut vs forced case
          // Only mark expired if we didn't initiate the sign-out ourselves
          // (The signOut helper in supabase.js will call supabase.auth.signOut
          //  which fires SIGNED_OUT — we can't easily distinguish, so we only
          //  set sessionExpired if the session was null before (token refresh fail)
          //  OR if forced. We use a flag approach via forceSignOut.)
        }
        setProfile(null)
        setRoleRow(null)
        setLoading(false)
      } else {
        loadProfile(u?.id ?? null)
      }

      prevUserRef.current = u
      setUser(u)
      setCurrentUserId(u?.id ?? null)
    })

    return () => subscription.unsubscribe()
  }, [loadProfile])

  const refreshProfile = useCallback(() => {
    // Returns the loadProfile promise so callers can `await` the refresh
    // (e.g. ProfileEditorModal awaits this before closing so reopening
    // doesn't show stale fields).
    if (user?.id) return loadProfile(user.id)
    return Promise.resolve()
  }, [user, loadProfile])

  // Mini-spec #61: any component can dispatch 'profile:refresh' to
  // force a re-fetch — used by the admin user-detail modal so plan /
  // unlimited flag changes show up without a sign-out/sign-in.
  useEffect(() => {
    function onRefresh() { if (user?.id) loadProfile(user.id) }
    window.addEventListener('profile:refresh', onRefresh)
    return () => window.removeEventListener('profile:refresh', onRefresh)
  }, [user?.id, loadProfile])

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false)
  }, [])

  // ── Role / scope helpers ────────────────────────────────────────────────
  // Single source of truth for "what can this user do in the CMS?":
  //   • isAdmin: DB role='admin' OR email allowlist (legacy fallback).
  //     The email allowlist stays as belt-and-suspenders during the
  //     migration window — once everyone's been seeded to user_roles
  //     it becomes redundant and can be removed.
  //   • isManager: DB role='manager'. Admins are NOT counted as
  //     managers (they're a higher tier); use hasAnyCmsAccess for
  //     "can they see the /admin route at all".
  //   • hasScope(scope): true for admin (implicit all), true for
  //     manager when scope is in their scopes array.
  //   • hasAnyCmsAccess: true for admin OR any manager with at
  //     least one scope. Used to gate the /admin route entrance.
  const isAdmin = !!(
    roleRow?.role === 'admin' ||
    (user?.email && isHardcodedAdminEmail(user.email))
  )
  const isManager = roleRow?.role === 'manager'
  const hasScope = useCallback((scope) => scopeIncludes(roleRow, scope), [roleRow])
  const hasAnyCmsAccess = !!(
    isAdmin ||
    (isManager && Array.isArray(roleRow?.scopes) && roleRow.scopes.length > 0)
  )

  // Manual refresh hook for the Team page — after admin grants /
  // revokes a role, dispatch this to update the current user's
  // permissions without a sign-out/sign-in.
  useEffect(() => {
    async function onRoleRefresh() {
      try { setRoleRow(await getMyRole()); } catch {}
    }
    window.addEventListener('role:refresh', onRoleRefresh)
    return () => window.removeEventListener('role:refresh', onRoleRefresh)
  }, [])

  return (
    <Ctx.Provider value={{
      user,
      profile,
      loading,
      sessionReady,
      refreshProfile,
      sessionExpired,
      setSessionExpired,
      clearSessionExpired,
      // Account type drives which surface the user gets: 'host' (full app),
      // 'guest' (trimmed), 'brand' (sponsor dashboard). Defaults to host.
      accountType: profile?.account_type || 'host',
      // Role / scope context — see the comment block above for semantics.
      role: roleRow?.role || null,
      scopes: roleRow?.scopes || [],
      isAdmin,
      isManager,
      hasScope,
      hasAnyCmsAccess,
    }}>
      {children}
    </Ctx.Provider>
  )
}

export const useAuth = () => useContext(Ctx)
